/** Client-side WebAuthn helpers (no external dependency; wraps navigator.credentials). */

export type TwoFactorChallengeInfo = {
  challengeId: string;
  methods: string[];
};

export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

export function base64UrlDecode(value: string): Uint8Array {
  const b64 = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function passkeysSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    typeof navigator !== "undefined" &&
    !!navigator.credentials
  );
}

type CredentialWithResponse = {
  id: string;
  rawId: ArrayBuffer;
  type: string;
  response: Record<string, ArrayBuffer | null | undefined>;
  getClientExtensionResults?: () => Record<string, unknown>;
};

function bufferToBase64Url(buf: ArrayBuffer | null | undefined): string {
  if (!buf) return "";
  return base64UrlEncode(new Uint8Array(buf));
}

/** Convert server registration options (b64url strings) to navigator args. */
export function toRegistrationArgs(options: {
  challenge: string;
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: string; alg: number }[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: { type: string; id: string }[];
}): CredentialCreationOptions {
  return {
    publicKey: {
      challenge: base64UrlDecode(options.challenge).buffer as ArrayBuffer,
      rp: options.rp,
      user: {
        id: base64UrlDecode(options.user.id).buffer as ArrayBuffer,
        name: options.user.name,
        displayName: options.user.displayName,
      },
      pubKeyCredParams: options.pubKeyCredParams.map((p) => ({
        type: "public-key" as const,
        alg: p.alg,
      })),
      timeout: options.timeout ?? 60000,
      attestation: options.attestation ?? "none",
      authenticatorSelection: options.authenticatorSelection,
      excludeCredentials: (options.excludeCredentials ?? []).map((c) => ({
        type: "public-key" as const,
        id: base64UrlDecode(c.id).buffer as ArrayBuffer,
      })),
    },
  };
}

/** Convert server authentication options to navigator args. */
export function toAuthenticationArgs(options: {
  challenge: string;
  rpId: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: { type: string; id: string }[];
}): CredentialRequestOptions {
  return {
    publicKey: {
      challenge: base64UrlDecode(options.challenge).buffer as ArrayBuffer,
      rpId: options.rpId,
      timeout: options.timeout ?? 60000,
      userVerification: options.userVerification ?? "preferred",
      allowCredentials: (options.allowCredentials ?? []).map((c) => ({
        type: "public-key" as const,
        id: base64UrlDecode(c.id).buffer as ArrayBuffer,
      })),
    },
  };
}

/** Serialize a new credential for POST /register/finish. */
export function serializeRegistrationCredential(
  credential: Credential,
): Record<string, unknown> {
  const c = credential as CredentialWithResponse;
  return {
    id: c.id,
    rawId: bufferToBase64Url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: bufferToBase64Url(
        c.response.clientDataJSON as ArrayBuffer,
      ),
      attestationObject: bufferToBase64Url(
        c.response.attestationObject as ArrayBuffer,
      ),
    },
  };
}

/** Serialize an assertion for POST verify / authenticate/finish. */
export function serializeAssertion(credential: Credential): string {
  const c = credential as CredentialWithResponse & {
    response: {
      authenticatorData: ArrayBuffer;
      signature: ArrayBuffer;
      userHandle: ArrayBuffer | null;
    };
  };
  return JSON.stringify({
    // credential.id from the browser is already base64url.
    id: c.id,
    rawId: bufferToBase64Url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: bufferToBase64Url(
        c.response.clientDataJSON as ArrayBuffer,
      ),
      authenticatorData: bufferToBase64Url(c.response.authenticatorData),
      signature: bufferToBase64Url(c.response.signature),
      userHandle: c.response.userHandle
        ? bufferToBase64Url(c.response.userHandle)
        : undefined,
    },
  });
}
